/**
 * Sales Profile Page
 *
 * Features:
 * - Sales user-specific profile information
 * - Real data fetched from Auth Service
 * - Clean, modern layout following Gaushala profile pattern
 * - Responsive design
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  AlertCircle,
  User,
  Phone,
  Mail,
  Shield,
  Calendar,
  X,
  Building2,
  Store,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { profileService, UserProfile } from '@/services/admin/profile.service';

interface ProfileDataItem {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  format?: (val: any) => string;
}

interface ProfileDataGridProps {
  title: string;
  subtitle?: string;
  items: ProfileDataItem[];
  columns?: number;
}

/**
 * Profile Data Grid Component
 */
const ProfileDataGrid: React.FC<ProfileDataGridProps> = ({ title, subtitle, items, columns = 2 }) => {
  return (
    <div>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
      </div>
      <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : ''} gap-6`}>
        {items.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center gap-2">
              {item.icon && <span className="text-gray-500">{item.icon}</span>}
              <span className="text-sm font-medium text-gray-600">{item.label}</span>
            </div>
            <div className="text-base font-semibold text-gray-900">
              {item.format ? item.format(item.value) : item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Sales Profile Page Component
 */
export const SalesProfile: React.FC = () => {
  // Navigation and Auth
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  // State management
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch profile on component mount
   */
  useEffect(() => {
    fetchProfile();
  }, []);

  /**
   * Fetch complete profile data
   */
  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const profileData = await profileService.getProfile();
      setProfile(profileData);

      if (profileData) {
        toast.success('Profile loaded successfully');
      }
    } catch (err) {
      let errorMessage = 'Failed to load profile';

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Close profile and navigate back to sales dashboard
   */
  const handleCloseProfile = () => {
    navigate('/sales');
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Build Personal Details data
   */
  const getPersonalDetailsData = (): ProfileDataItem[] => {
    const fullName = authUser?.name || profile?.name || '—';
    const phoneNumber = authUser?.phone || profile?.phone || '—';
    const emailAddress = authUser?.email || profile?.email || '—';
    const userId = authUser?.id || profile?.id || '—';
    const accountCreated = profile?.createdAt || '—';
    const lastLogin = profile?.lastLogin || '—';
    const kycStatus = authUser?.kycStatus || profile?.kycStatus || '—';
    const accountStatus = authUser?.isActive !== undefined
      ? (authUser.isActive ? 'Active' : 'Inactive')
      : (profile?.isActive !== undefined ? (profile.isActive ? 'Active' : 'Inactive') : '—');

    return [
      { label: 'Full Name', value: fullName, icon: <User className="w-4 h-4" /> },
      { label: 'Phone Number', value: phoneNumber, icon: <Phone className="w-4 h-4" /> },
      { label: 'Email Address', value: emailAddress, icon: <Mail className="w-4 h-4" /> },
      { label: 'User ID', value: userId, icon: <Shield className="w-4 h-4" /> },
      {
        label: 'Account Created',
        value: accountCreated,
        icon: <Calendar className="w-4 h-4" />,
        format: (val) => val === '—' ? '—' : formatDate(val)
      },
      {
        label: 'Last Login',
        value: lastLogin,
        icon: <Calendar className="w-4 h-4" />,
        format: (val) => val === '—' ? '—' : formatDate(val)
      },
      { label: 'KYC Status', value: kycStatus },
      { label: 'Account Status', value: accountStatus },
    ];
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error && !profile) {
    return (
      <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex items-center justify-center">
          <Card className="p-6 lg:p-8 bg-white rounded-xl shadow-md w-full max-w-md">
            <Alert variant="destructive" className="mb-6 border-3 rounded-lg border-red-300 bg-red-50">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-700" />
              <div className="ml-3 flex-1 text-red-900">
                <h3 className="font-semibold">Error Loading Profile</h3>
                <p className="text-sm mt-2">{error}</p>
              </div>
            </Alert>

            <Button onClick={fetchProfile} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg mb-3">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  /**
   * Main profile display
   */
  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="flex-1 overflow-hidden px-0 py-4 lg:py-6">
        <div className="w-full h-full flex flex-col gap-4">
          {/* Profile Header Card */}
          <Card className="p-6 lg:p-8 bg-white rounded-lg shadow-sm border border-gray-200 flex-shrink-0 mx-4 sm:mx-6 lg:mx-8 relative">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseProfile}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 h-9 w-9 rounded-lg transition-all"
              title="Close profile"
            >
              <X className="w-5 h-5" />
            </Button>

            <div className="flex flex-col sm:flex-row gap-8">
              {/* Profile Photo */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center border-4 border-blue-600 shadow-lg">
                  <Store className="w-16 h-16 sm:w-14 sm:h-14 text-blue-600" />
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1">
                <div className="mb-6">
                  <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {authUser?.name || profile?.name || 'Sales Manager'}
                  </h2>
                  <p className="text-gray-600 text-base font-medium">Sales Operations</p>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-3">
                  {profile?.kycStatus && (
                    <Badge className="bg-blue-100 text-blue-800 border-0 text-sm font-semibold px-3 py-1.5">
                      {profile.kycStatus}
                    </Badge>
                  )}
                  {profile?.isActive && (
                    <Badge className="bg-green-100 text-green-800 border-0 text-sm font-semibold px-3 py-1.5">
                      Active
                    </Badge>
                  )}
                  {profile?.roles?.map((role, idx) => (
                    <Badge key={idx} className="bg-purple-100 text-purple-800 border-0 text-sm font-semibold px-3 py-1.5">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Refresh Button */}
              {error && (
                <Button onClick={fetchProfile} variant="outline" className="self-start lg:self-center h-fit">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>

            {/* Separator */}
            <Separator className="mt-8" />
          </Card>

          {/* Profile Details Card */}
          <Card className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col overflow-hidden mx-4 sm:mx-6 lg:mx-8">
            <div className="p-6 lg:p-8 overflow-y-auto flex-1">
              <ProfileDataGrid
                title="Personal Information"
                subtitle="Your account details and status"
                items={getPersonalDetailsData()}
                columns={2}
              />

              {/* Roles Section */}
              {profile?.roles && profile.roles.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Assigned Roles</h3>
                  <div className="flex flex-wrap gap-3">
                    {profile.roles.map((role, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-800 border-0 px-3 py-1.5 text-sm font-semibold">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Permissions Section */}
              {profile?.permissions && profile.permissions.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Permissions</h3>
                  <div className="flex flex-wrap gap-3">
                    {profile.permissions.map((perm, idx) => (
                      <Badge key={idx} variant="outline" className="border-gray-300 bg-gray-50 px-3 py-1.5 text-sm font-semibold">
                        {perm}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Last Updated Footer */}
            {profile?.createdAt && (
              <div className="text-center text-sm text-gray-500 border-t border-gray-200 px-6 lg:px-8 py-4 flex-shrink-0 bg-gray-50">
                Account created: {formatDate(profile.createdAt)}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SalesProfile;
